import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, FloppyDisk, Sparkle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// One-time welcome for freshly registered Clientes: invite them to complete
// address, phone, and body measurements so shipments run smoothly.
const EMPTY = {
  full_name: "",
  phone_mobile: "",
  whatsapp: "",
  cep: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  shirt_size: "",
  pants_size: "",
  dress_size: "",
  shoe_size: "",
  bust: "",
  waist: "",
  hip: "",
};

function isIncomplete(cust) {
  if (!cust) return false;
  const needsAddress = !cust.cep || !cust.street || !cust.number;
  const needsContact = !cust.phone_mobile && !cust.whatsapp;
  return needsAddress || needsContact;
}

export default function WelcomeCustomerModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);

  const dismissKey = user ? `welcome_dismissed_${user.real_user_id || user.user_id}` : null;

  const checkProfile = useCallback(async () => {
    if (!user) return;
    // Only for Clientes (has handle, not master)
    if (user.is_master || !user.customer_handle) return;
    // Respect a previous dismissal
    if (dismissKey && localStorage.getItem(dismissKey) === "1") return;
    try {
      const { data } = await api.get("/me/customer");
      if (isIncomplete(data)) {
        setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, data[k] || ""])) });
        setOpen(true);
      }
    } catch (err) {
      // Expected: user has no customer record yet (e.g. race after first login) — skip silently.
      if (process.env.NODE_ENV === "development") console.debug("[Welcome] no customer profile", err);
    }
  }, [user, dismissKey]);

  useEffect(() => { checkProfile(); }, [checkProfile]);

  const lookupCep = async (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setLoadingCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const j = await resp.json();
      if (!j.erro) {
        setForm(f => ({
          ...f,
          street: j.logradouro || f.street,
          neighborhood: j.bairro || f.neighborhood,
          city: j.localidade || f.city,
          state: j.uf || f.state,
        }));
      }
    } catch (err) {
      // ViaCEP is best-effort autofill; if it fails (network, invalid CEP), user just types the address.
      if (process.env.NODE_ENV === "development") console.debug("[Welcome] ViaCEP lookup failed", err);
    } finally { setLoadingCep(false); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Send only non-empty fields to keep the payload small
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => String(v).trim() !== ""),
      );
      await api.put("/me/customer", payload);
      if (dismissKey) localStorage.setItem(dismissKey, "1");
      toast.success("Cadastro atualizado, obrigada! Isso agiliza os envios.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const dismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-brand-ink/60 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={dismiss}>
      <div data-testid="welcome-customer-modal" className="card-brutal w-full max-w-3xl bg-white my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 bg-brand-pink text-white flex justify-between items-start">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest opacity-90">
              <Sparkle weight="bold" size={12} className="inline mr-1" />Bem-vinda ao Desapegue com Herida
            </p>
            <h2 className="font-heading text-3xl font-black mt-1">Vamos deixar seus envios prontos?</h2>
            <p className="text-sm mt-2 opacity-90">Complete seus dados de contato, endereço e medidas — assim quando você comprar na live já enviamos direto pra você, sem retrabalho.</p>
          </div>
          <button data-testid="welcome-close-btn" onClick={dismiss} className="p-2 border-2 border-white hover:bg-white hover:text-brand-pink">
            <X weight="bold" size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <section>
            <p className="label-brutal">Contato</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
              <Field label="Nome completo" testid="welcome-full-name" value={form.full_name} onChange={(v) => setField("full_name", v)} />
              <Field label="Celular" testid="welcome-phone" value={form.phone_mobile} onChange={(v) => setField("phone_mobile", v)} placeholder="(11) 91234-5678" />
              <Field label="WhatsApp" testid="welcome-whatsapp" value={form.whatsapp} onChange={(v) => setField("whatsapp", v)} placeholder="(11) 91234-5678" />
            </div>
          </section>

          <section>
            <p className="label-brutal">Endereço para envio</p>
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_120px] gap-3 mt-1">
              <Field
                label="CEP"
                testid="welcome-cep"
                value={form.cep}
                onChange={(v) => { setField("cep", v); if (v.replace(/\D/g, "").length === 8) lookupCep(v); }}
                placeholder="00000-000"
                hint={loadingCep ? "Buscando…" : undefined}
              />
              <Field label="Rua" testid="welcome-street" value={form.street} onChange={(v) => setField("street", v)} />
              <Field label="Número" testid="welcome-number" value={form.number} onChange={(v) => setField("number", v)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_60px] gap-3 mt-3">
              <Field label="Complemento" testid="welcome-complement" value={form.complement} onChange={(v) => setField("complement", v)} />
              <Field label="Bairro" testid="welcome-neighborhood" value={form.neighborhood} onChange={(v) => setField("neighborhood", v)} />
              <Field label="Cidade" testid="welcome-city" value={form.city} onChange={(v) => setField("city", v)} />
              <Field label="UF" testid="welcome-state" value={form.state} onChange={(v) => setField("state", v.toUpperCase().slice(0, 2))} />
            </div>
          </section>

          <section>
            <p className="label-brutal">Suas medidas <span className="text-brand-ink2 font-normal normal-case ml-1">(opcional, ajuda a encontrar peças que servem)</span></p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1">
              <Field label="Blusa (P/M/G)" testid="welcome-shirt" value={form.shirt_size} onChange={(v) => setField("shirt_size", v)} />
              <Field label="Calça (36-52)" testid="welcome-pants" value={form.pants_size} onChange={(v) => setField("pants_size", v)} />
              <Field label="Vestido" testid="welcome-dress" value={form.dress_size} onChange={(v) => setField("dress_size", v)} />
              <Field label="Calçado" testid="welcome-shoe" value={form.shoe_size} onChange={(v) => setField("shoe_size", v)} />
              <Field label="Busto (cm)" testid="welcome-bust" value={form.bust} onChange={(v) => setField("bust", v)} />
              <Field label="Cintura (cm)" testid="welcome-waist" value={form.waist} onChange={(v) => setField("waist", v)} />
              <Field label="Quadril (cm)" testid="welcome-hip" value={form.hip} onChange={(v) => setField("hip", v)} />
            </div>
          </section>
        </div>

        <div className="p-4 border-t-4 border-brand-ink flex flex-wrap justify-between gap-2 bg-brand-cream">
          <button data-testid="welcome-skip-btn" onClick={dismiss} className="btn-white h-[52px]">Depois eu preencho</button>
          <button data-testid="welcome-save-btn" onClick={save} disabled={saving} className="btn-pink h-[52px] disabled:opacity-60">
            <FloppyDisk weight="bold" size={16} /> {saving ? "Salvando…" : "Salvar cadastro"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, hint }) {
  return (
    <div>
      <label className="label-brutal">{label}</label>
      <input
        data-testid={testid}
        className="input-brutal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <p className="text-[11px] text-brand-ink2 mt-1">{hint}</p>}
    </div>
  );
}
